import { BridgeComponent } from '@hotwired/hotwire-native-bridge';
import { say } from '../../lib/text';

export default class extends BridgeComponent {
  static component = 'location';

  // One handler per event, made once: removeEventListener only removes the
  // very function addEventListener got, and a fresh arrow or .bind(this) is
  // never that function — so stop never stopped and every start stacked.
  onRequest = () => this.requestLocation();
  onStart = () => this.startLiveLocation();
  onStop = () => this.stopLiveLocation();

  connect() {
    super.connect();
    this.element.addEventListener('location:requestLocation', this.onRequest);
    this.element.addEventListener('location:startLiveLocation', this.onStart);
    this.element.addEventListener('location:stopLiveLocation', this.onStop);
  }

  requestLocation() {
    console.log('Requesting location from native component');

    this.send('requestLocation', {}, ({ data: { result, latitude, longitude, error } }) => {
      if (result && latitude && longitude) {
        console.log('Location received:', { latitude, longitude });
        this.updateUserLocation(latitude, longitude);
      } else {
        console.error('Location request failed:', error);
        this.handleShellError(error);
      }
    });
  }

  startLiveLocation() {
    console.log('Starting live location tracking from native component');

    this.send('startLiveLocation', {}, ({ data: { result, error } }) => {
      if (result) {
        console.log('Live location tracking started');
        this.handleLiveLocationStarted();
      } else {
        console.error('Failed to start live location tracking:', error);
        this.handleShellError(error);
      }
    });

    // The shell can only reply to a message the page sent, and it replies to
    // the last one per event: every fix comes back to this callback.
    this.live = true;
    this.send('liveLocationUpdate', {}, (message) => this.handleLiveLocationUpdate(message));
  }

  stopLiveLocation() {
    console.log('Stopping live location tracking');
    this.live = false;

    this.send('stopLiveLocation', {}, ({ data: { result, error } }) => {
      if (result) {
        console.log('Live location tracking stopped');
        this.handleLiveLocationStopped();
      } else {
        console.error('Failed to stop live location tracking:', error);
        this.handleShellError(error);
      }
    });
  }

  handleLiveLocationUpdate({ data: { latitude, longitude } }) {
    if (this.live && latitude && longitude) {
      console.log('Live location update received:', { latitude, longitude });
      this.updateUserLocation(latitude, longitude);
    }
  }

  disconnect() {
    this.element.removeEventListener('location:requestLocation', this.onRequest);
    this.element.removeEventListener('location:startLiveLocation', this.onStart);
    this.element.removeEventListener('location:stopLiveLocation', this.onStop);
    super.disconnect();
  }

  updateUserLocation(latitude, longitude) {
    console.log('Updating user location via bridge:', { latitude, longitude });

    fetch('/location', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
      body: JSON.stringify({ lat: latitude, lng: longitude }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log('Location updated successfully via bridge');
          this.handleLocationUpdated({ latitude, longitude });
        } else {
          console.error('Failed to update location:', data.error);
          this.handleLocationError(data.error);
        }
      })
      .catch((error) => {
        console.error('Error updating location:', error);
        this.handleLocationError(say('error'));
      });
  }

  // Event handlers for UI
  handleLocationUpdated(data) {
    // Dispatch custom event for UI controllers to handle
    const event = new CustomEvent('location:locationUpdated', {
      detail: data,
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  handleLiveLocationStarted() {
    // Dispatch custom event for UI controllers to handle
    const event = new CustomEvent('location:liveLocationStarted', {
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  handleLiveLocationStopped() {
    // Dispatch custom event for UI controllers to handle
    const event = new CustomEvent('location:liveLocationStopped', {
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  // The shell sends a code (disabled, denied, unavailable), never words: the
  // page says it in its own language, and an unknown code is the generic error.
  handleShellError(code) {
    this.handleLocationError(say(`location.${code}`) || say('error'));
  }

  handleLocationError(error) {
    // Dispatch custom event for UI controllers to handle
    const event = new CustomEvent('location:locationError', {
      detail: { error },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }
}
